import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { draftAPI } from '../../utilities/api';
import { Draft } from '../../utilities/interfaces/Draft';

// represents the shape of the slice in state
interface DraftState {
  value: any;
  filingDrafts: any;
  currentFilteredDrafts: any;
  fetchingValues: boolean;
  historicalDrafts: any;
  fetchingHistorical: boolean;
}

const initialState: DraftState = {
  value: [],
  filingDrafts: [],
  currentFilteredDrafts: [],
  fetchingValues: false,
  historicalDrafts: [],
  fetchingHistorical: false,
};

const draftSlice = createSlice({
  name: 'stateDraft',
  initialState,
  reducers: {
    // add new Draft item
    addDraftItem(state, action: PayloadAction<Draft>) {
      // its okay to do this because immer makes it immutable
      state.value.push(action.payload);
    },
  },
  extraReducers(builder) {
    builder.addCase(getAllDrafts.pending, (state) => {
      state.fetchingValues = true;
    });
    builder.addCase(getAllDrafts.fulfilled, (state, action) => {
      state.value = action.payload;
      state.fetchingValues = false;
    });
    builder.addCase(getAllDrafts.rejected, (state) => {
      state.fetchingValues = false;
    });
    builder.addCase(getAllDraftsByTitleId.pending, (state, action) => {
      state.filingDrafts = [];
    });
    builder.addCase(getAllDraftsByTitleId.fulfilled, (state, action) => {
      state.filingDrafts = action.payload;
    });
    builder.addCase(createDraft.fulfilled, (state, action) => {
      state.value.push(action.payload);
    });
    builder.addCase(getHistoricalDrafts.pending, (state) => {
      state.fetchingHistorical = true;
    });
    builder.addCase(getHistoricalDrafts.fulfilled, (state, action) => {
      state.historicalDrafts = action.payload;
      state.fetchingHistorical = false;
    });
    builder.addCase(getHistoricalDrafts.rejected, (state) => {
      state.fetchingHistorical = false;
    });

    builder.addCase(getCurrentFilteredDrafts.pending, (state) => {
      state.fetchingValues = true;
    });
    builder.addCase(getCurrentFilteredDrafts.fulfilled, (state, action) => {
      state.currentFilteredDrafts = action.payload;
      state.fetchingValues = false;
    });
    builder.addCase(getCurrentFilteredDrafts.rejected, (state) => {
      state.fetchingValues = false;
    });
  },
});

// Async thunk to fetch data from api
// under extra reducers on fulfilled the redux store is updated
export const getAllDrafts = createAsyncThunk('stateDraft/getDraftsItems', async () => {
  return await draftAPI.getAllDrafts();
});

export const getCurrentFilteredDrafts = createAsyncThunk('stateDraft/getCurrentFiltered', async () => {
  return await draftAPI.getCurrentFilteredDrafts();
});

export const getAllDraftsByTitleId = createAsyncThunk('stateDraft/getDraftsByTitleId', async (titleId: number) => {
  return await draftAPI.getDraftsByTitleId(titleId);
});

export const createDraft = createAsyncThunk('stateDraft/createDraft', async (draft: Draft) => {
  return await draftAPI.createDraft(draft);
});

export const deleteDraftById = createAsyncThunk('stateDraft/deleteDraftById', async (id: number) => {
  return await draftAPI.deleteDraftById(id);
});

export const getHistoricalDrafts = createAsyncThunk('stateDraft/GetHistoricalDrafts', async () => {
  return await draftAPI.getHistorical();
});

export const { addDraftItem } = draftSlice.actions;
export default draftSlice.reducer;
